import { useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useTranslation } from "react-i18next";
import {
  ArrowUp,
  Check,
  CheckCircle2,
  Clock,
  Feather,
  Gauge,
  Link2,
  ListPlus,
  MessageCircle,
  Moon,
  Orbit,
  Rocket,
  Sparkles,
  Sun,
  Sunrise,
  Target,
  X,
} from "lucide-react";

import { cn } from "../../../lib/utils";
import { queryClient } from "../../../lib/queryClient";
import useRetryFetch from "../../../hooks/useRetryFetch";
import { useEffectOnce } from "../../../hooks/useEffectOnce";
import { useRitualDay } from "../../../hooks/useRitualDay";
import { dueRitual } from "../../../lib/rituals";
import {
  executeRitualSuggestions,
  getOpenRitualSession,
  ritualChat,
  ritualClose,
  type RitualExecuteResult,
  type RitualKey,
  type RitualMessage,
  type RitualSuggestion,
} from "../../../api/lifeOs";
import { Button } from "../../ui/button";
import { Checkbox } from "../../ui/checkbox";

// ── Rituals v1 — the companion-led sessions (Life OS Phase B) ────────────────
// The RitualCard on Today knows which ritual is due from the preferences the
// Seeder stored (config.cockpit.rituals) and what already ran (ritualLog).
// The RitualSession is a short conversation with a Claude-style contract:
// the companion asks, the user talks, and concrete changes arrive as a typed
// suggestion batch the user APPROVES with a tap — nothing executes from
// conversation alone.
//
// Design identity: a ritual is a guided journal page, not a chat widget. The
// companion speaks in open serif text on the page itself; only the user's
// replies are contained. Each ritual carries its own light (dawn amber, dusk
// indigo, weekly brand, check-in neutral) over the same structure, so the
// moment feels distinct while staying unmistakably ClaryNext.
//
// The session is portaled to document.body: mounted inline it would sit as a
// sibling inside Today's `space-y-8` column, which stamps a margin-top on
// every non-first child — including this one, despite `fixed inset-0` — and
// visibly breaks full-viewport coverage. A portal sidesteps layout inheritance
// entirely.

// Schedule logic (due / done on the person's local day) lives in lib/rituals
// so RitualCard, the rhythm line and For now share one implementation.
export { dueRitual };

// ── Mood — each ritual gets its own light over the same structure ────────────
// Built from the app's existing tokens (brand primary + Tailwind's standard
// palette): the same product, a different moment of day.

interface Mood {
  Icon: React.ElementType;
  wash: string; // ambient gradient wash behind the whole session
  blobA: string; // soft ambient blur — upper field
  blobB: string; // soft ambient blur — lower field
  ring: string; // icon halo (bg + text)
  hairline: string; // gradient rule under the header
  glow: string; // halo while the companion is composing / on the closing seal
  card: string; // suggestion-card accent border
  accent: string; // small accents (unread marker, orb)
}

const MOOD: Record<RitualKey, Mood> = {
  morning: {
    Icon: Sun,
    wash: "from-amber-50 via-background to-background",
    blobA: "bg-amber-200/40",
    blobB: "bg-orange-100/40",
    ring: "bg-amber-100 text-amber-600",
    hairline: "via-amber-300/60",
    glow: "shadow-[0_0_32px_8px_rgba(245,158,11,0.28)]",
    card: "border-amber-200/80",
    accent: "bg-amber-400",
  },
  evening: {
    Icon: Moon,
    wash: "from-indigo-50 via-background to-background",
    blobA: "bg-indigo-200/35",
    blobB: "bg-violet-100/40",
    ring: "bg-indigo-100 text-indigo-600",
    hairline: "via-indigo-300/60",
    glow: "shadow-[0_0_32px_8px_rgba(99,102,241,0.25)]",
    card: "border-indigo-200/80",
    accent: "bg-indigo-400",
  },
  weekly: {
    Icon: Sparkles,
    wash: "from-primary/10 via-background to-background",
    blobA: "bg-primary/20",
    blobB: "bg-emerald-100/50",
    ring: "bg-primary/15 text-primary",
    hairline: "via-primary/40",
    glow: "shadow-[0_0_32px_8px_hsl(var(--primary)/0.28)]",
    card: "border-primary/30",
    accent: "bg-primary",
  },
  checkin: {
    Icon: MessageCircle,
    wash: "from-muted via-background to-background",
    blobA: "bg-primary/10",
    blobB: "bg-slate-200/30",
    ring: "bg-primary/10 text-primary",
    hairline: "via-border",
    glow: "shadow-[0_0_32px_8px_hsl(var(--primary)/0.18)]",
    card: "border-primary/20",
    accent: "bg-primary/70",
  },
};

// Every proposed change gets a face — the batch reads as a set of small acts,
// not a wall of checkbox text.
const SUGGESTION_ICON: Record<string, React.ElementType> = {
  create_action: ListPlus,
  complete_action: CheckCircle2,
  defer_action: Clock,
  save_reflection: Feather,
  create_project: Rocket,
  attach_capture: Link2,
  create_space: Orbit,
  evolve_space_identity: Orbit,
  update_horizon: Sunrise,
  log_gap: Target,
  set_active_cap: Gauge,
};

const suggestionIcon = (s: RitualSuggestion): React.ElementType => {
  if (s.kind === "place_project") return s.placement === "active" ? Sun : Moon;
  return SUGGESTION_ICON[s.kind] ?? Check;
};

// The companion writes prose on the page; the model sometimes emphasizes with
// markdown. Render **bold** / *italic* inline so the marks never show raw.
const renderProse = (text: string): React.ReactNode[] =>
  text.split(/(\*\*[^*]+\*\*|\*[^*\n]+\*)/g).map((seg, i) => {
    if (seg.startsWith("**") && seg.endsWith("**") && seg.length > 4)
      return (
        <strong key={i} className="font-semibold">
          {seg.slice(2, -2)}
        </strong>
      );
    if (seg.startsWith("*") && seg.endsWith("*") && seg.length > 2)
      return <em key={i}>{seg.slice(1, -1)}</em>;
    return seg;
  });

const ON_DEMAND: RitualKey[] = ["morning", "evening", "weekly"];

// ── Card on Today ─────────────────────────────────────────────────────────────

export const RitualCard = () => {
  const { t } = useTranslation();
  const [open, setOpen] = useState<RitualKey | null>(null);
  // Only users who have set up the Life OS (a compass exists) get the companion.
  const { hasCompass, prefs, log } = useRitualDay();
  const due = useMemo(() => dueRitual(prefs, log, new Date()), [prefs, log]);

  if (!hasCompass && !open) return null;

  return (
    <>
      {due && !open ? (
        <DueCard due={due} onBegin={() => setOpen(due)} />
      ) : !open ? (
        <AnytimeCard onOpen={setOpen} />
      ) : null}
      {open && <RitualSession ritualKey={open} onClose={() => setOpen(null)} />}
    </>
  );
};

const DueCard: React.FC<{ due: RitualKey; onBegin: () => void }> = ({ due, onBegin }) => {
  const { t } = useTranslation();
  const { Icon, ring } = MOOD[due];
  return (
    <div className="rounded-2xl border border-primary/25 bg-gradient-to-br from-primary/10 via-card to-card p-4">
      {/* Stacked (not one row) so the card reads well in the 332px home rail */}
      <div className="flex items-start gap-3">
        <div className={cn("rounded-full p-2.5 flex-shrink-0", ring)}>
          <Icon className="w-5 h-5" />
        </div>
        <div className="flex-1 min-w-0">
          <h3 className="text-sm font-semibold text-foreground">{t(`ritual_ready_${due}`)}</h3>
          <p className="text-xs text-muted-foreground mt-0.5">{t(`ritual_line_${due}`)}</p>
        </div>
      </div>
      <Button size="sm" className="rounded-full w-full mt-3" onClick={onBegin}>
        {t("ritual_begin")}
      </Button>
    </div>
  );
};

// The companion is never only-on-schedule: an open check-in is one tap away,
// and any scripted ritual can be run on demand.
const AnytimeCard: React.FC<{ onOpen: (k: RitualKey) => void }> = ({ onOpen }) => {
  const { t } = useTranslation();
  return (
    <div className="rounded-2xl border border-gray-200 bg-card p-4">
      {/* Stacked (not one row) so the card reads well in the 332px home rail */}
      <div className="flex items-start gap-3">
        <div className="rounded-full bg-primary/10 p-2 flex-shrink-0">
          <MessageCircle className="w-4 h-4 text-primary" />
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-sm font-medium text-foreground">{t("ritual_anytime_title")}</p>
          <p className="text-xs text-muted-foreground mt-0.5">{t("ritual_anytime_text")}</p>
        </div>
      </div>
      <div className="flex items-center justify-between gap-2 mt-3">
        <div className="flex items-center gap-1.5">
          {ON_DEMAND.map((k) => {
            const Icon = MOOD[k].Icon;
            return (
              <button
                key={k}
                onClick={() => onOpen(k)}
                title={t(`seed_ritual_${k}`)}
                className="p-2 rounded-full border border-gray-200 text-muted-foreground hover:text-primary hover:border-primary/40 transition-colors cursor-pointer"
              >
                <Icon className="w-3.5 h-3.5" />
              </button>
            );
          })}
        </div>
        <Button size="sm" className="rounded-full" onClick={() => onOpen("checkin")}>
          {t("ritual_checkin_cta")}
        </Button>
      </div>
    </div>
  );
};

// ── The session ───────────────────────────────────────────────────────────────

// The [[approved]] prefix marks the hidden continuity note that keeps the
// companion aware of what the user approved — it is sent, never rendered.
const APPROVED_MARK = "[[approved]] ";

interface SessionMessage extends RitualMessage {
  suggestions?: RitualSuggestion[];
  results?: RitualExecuteResult[];
  decided?: boolean;
}

export const RitualSession: React.FC<{ ritualKey: RitualKey; onClose: () => void }> = ({
  ritualKey,
  onClose,
}) => {
  const { t, i18n } = useTranslation();
  const retryFetch = useRetryFetch();
  const lang = (i18n.language || "en").slice(0, 2);
  const mood = MOOD[ritualKey];
  const Icon = mood.Icon;

  const [messages, setMessages] = useState<SessionMessage[]>([]);
  const [picked, setPicked] = useState<Record<number, boolean[]>>({});
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);
  const [closed, setClosed] = useState(false);
  const [intention, setIntention] = useState<string | null>(null);
  const [error, setError] = useState(false);
  const scrollRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const todayLine = useMemo(
    () =>
      new Date().toLocaleDateString(i18n.language || "en", {
        weekday: "long",
        month: "long",
        day: "numeric",
      }),
    [i18n.language]
  );

  const scrollDown = () =>
    setTimeout(() => scrollRef.current?.scrollTo({ top: 99999, behavior: "smooth" }), 60);

  const history = (msgs: SessionMessage[]): RitualMessage[] =>
    msgs.map(({ role, content }) => ({ role, content }));

  const turn = async (hist: RitualMessage[]) => {
    setBusy(true);
    setError(false);
    try {
      const res = await ritualChat(retryFetch, ritualKey, hist, lang);
      setMessages((m) => {
        const idx = m.length;
        if (res.suggestions.length > 0) {
          setPicked((p) => ({ ...p, [idx]: res.suggestions.map(() => true) }));
        }
        return [
          ...m,
          { role: "assistant", content: res.text, suggestions: res.suggestions },
        ];
      });
      if (res.closed) {
        setClosed(true);
        setIntention(res.intention);
        queryClient.invalidateQueries();
      }
    } catch {
      setError(true);
    } finally {
      setBusy(false);
      scrollDown();
      // preventScroll: focusing must never scroll the overlay itself — the
      // ambient blobs overhang it, and a focus-reveal scroll would wedge the
      // whole session off-center inside its own clipped box.
      requestAnimationFrame(() => inputRef.current?.focus({ preventScroll: true }));
    }
  };

  useEffectOnce(() => {
    // Transcripts are persisted server-side on every turn — if a fresh
    // session is still open (refresh, crashed tab), pick it up where it
    // left off instead of starting over.
    (async () => {
      try {
        const open = await getOpenRitualSession(retryFetch, ritualKey);
        if (open && open.messages.length > 0) {
          const last = open.messages.length - 1;
          const restored: SessionMessage[] = open.messages.map((m, i) => ({
            role: m.role,
            content: m.content,
            ...(m.suggestions && m.suggestions.length > 0
              ? // Only the final message's proposal is still actionable —
                // earlier ones were already answered (or superseded) in-flow.
                { suggestions: m.suggestions, decided: i < last }
              : {}),
          }));
          const tail = restored[last];
          if (tail?.suggestions && !tail.decided) {
            setPicked({ [last]: tail.suggestions.map(() => true) });
          }
          setMessages(restored);
          scrollDown();
          requestAnimationFrame(() => inputRef.current?.focus({ preventScroll: true }));
          return;
        }
      } catch {
        /* resume is best-effort — fall through to a fresh session */
      }
      turn([]);
    })();
  });

  const send = (e?: React.FormEvent) => {
    e?.preventDefault();
    const text = input.trim();
    if (!text || busy) return;
    const next: SessionMessage[] = [...messages, { role: "user", content: text }];
    setMessages(next);
    setInput("");
    scrollDown();
    turn(history(next));
  };

  const approve = async (msgIdx: number) => {
    const msg = messages[msgIdx];
    if (!msg?.suggestions || busy) return;
    const selection = picked[msgIdx] ?? msg.suggestions.map(() => true);
    const chosen = msg.suggestions.filter((_, i) => selection[i]);
    if (chosen.length === 0) {
      skip(msgIdx);
      return;
    }
    setBusy(true);
    try {
      const results = await executeRitualSuggestions(retryFetch, chosen);
      const done = results.filter((r) => r.ok);
      const failed = results.filter((r) => !r.ok);
      // The model must see failures too — a capped placement that silently
      // vanished from this note left it thinking everything landed.
      const note =
        APPROVED_MARK +
        (done.length > 0
          ? "Already approved and executed by the system — do not re-suggest: " +
            done.map((r) => r.label).join("; ") +
            ". "
          : "") +
        (failed.length > 0
          ? "FAILED (not executed — address in conversation, do not blindly re-suggest): " +
            failed.map((r) => `${r.label} — ${r.error ?? "unknown error"}`).join("; ")
          : "");
      // Keep the note even on an already-closed session: there's no further
      // server round-trip for it to ride (the composer is gone), so failures
      // there surface through the result chips instead — but keeping the
      // note in local state costs nothing and keeps the transcript consistent.
      setMessages((m) =>
        m.map((x, i) =>
          i === msgIdx ? { ...x, results, decided: true } : x
        ).concat([{ role: "user", content: note }])
      );
      queryClient.invalidateQueries();
    } catch {
      setError(true);
    } finally {
      setBusy(false);
      scrollDown();
    }
  };

  const skip = (msgIdx: number) => {
    setMessages((m) =>
      m.map((x, i) => (i === msgIdx ? { ...x, decided: true, results: [] } : x))
    );
  };

  const endEarly = async () => {
    if (closed) {
      onClose();
      return;
    }
    setBusy(true);
    try {
      await ritualClose(retryFetch, ritualKey, history(messages), lang);
    } catch {
      /* the log entry is best-effort on early exit */
    }
    queryClient.invalidateQueries();
    onClose();
  };

  const overlay = (
    // overflow-clip (not -hidden): the decorative blobs overhang the box, and a
    // clipped box cannot be scrolled at all — hidden boxes can, so any
    // focus-reveal scroll would shift the session and stick.
    <div
      className="fixed inset-0 z-[70] flex flex-col overflow-clip"
      role="dialog"
      aria-modal="true"
      aria-label={t(`seed_ritual_${ritualKey}`)}
      onKeyDown={(e) => {
        if (e.key === "Escape") endEarly();
      }}
    >
      {/* Solid base FIRST: a gradient's early stops are inherently translucent
          blends, not its final color, so relying on the wash alone left the
          page behind visible through roughly the top half of the session
          (confirmed by rendering real content behind it — the gradient only
          reaches true opacity well past its midpoint). This guarantees full
          opacity at every pixel; the wash above it stays purely decorative. */}
      <div className="absolute inset-0 bg-background" aria-hidden="true" />
      {/* Ambient atmosphere — mood wash + soft blurred fields, purely decorative */}
      <div className={cn("absolute inset-0 bg-gradient-to-b pointer-events-none", mood.wash)} aria-hidden="true">
        <div className={cn("absolute -top-24 -end-24 h-80 w-80 rounded-full blur-3xl motion-reduce:animate-none animate-companion-float", mood.blobA)} />
        <div className={cn("absolute -bottom-32 -start-16 h-96 w-96 rounded-full blur-3xl", mood.blobB)} />
      </div>

      <div className="relative max-w-2xl mx-auto w-full flex-1 flex flex-col min-h-0 px-6 pt-5 pb-4">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className={cn("rounded-full p-2.5 motion-reduce:animate-none animate-companion-float", mood.ring)}>
              <Icon className="w-5 h-5" />
            </div>
            <div>
              <p className="text-[15px] font-semibold text-foreground leading-tight tracking-tight">
                {t(`seed_ritual_${ritualKey}`)}
              </p>
              <p className="text-[11px] text-muted-foreground/80 leading-tight mt-0.5 capitalize">
                {todayLine}
              </p>
            </div>
          </div>
          <button
            onClick={endEarly}
            aria-label={closed ? t("ritual_done") : t("ritual_end")}
            className="inline-flex items-center gap-1.5 text-xs font-medium text-muted-foreground hover:text-foreground px-3 py-2 rounded-full hover:bg-black/5 transition-colors cursor-pointer"
          >
            {closed ? <X className="w-4 h-4" /> : t("ritual_end")}
          </button>
        </div>

        {/* Mood hairline */}
        <div className={cn("h-px mt-4 bg-gradient-to-r from-transparent to-transparent", mood.hairline)} aria-hidden="true" />

        {/* Transcript */}
        <div ref={scrollRef} className="flex-1 overflow-y-auto pt-6 pb-6 space-y-5 scroll-smooth no-scrollbar">
          {messages.map((m, i) => {
            if (m.role === "user" && m.content.startsWith(APPROVED_MARK)) return null;

            if (m.role === "assistant") {
              // The companion writes on the page itself — open serif text,
              // marked only by a small tinted glyph. A journal, not a chat log.
              return (
                <div key={i} className="animate-companion-enter motion-reduce:animate-none">
                  <div className="flex gap-3">
                    <div className={cn("mt-1 h-7 w-7 flex-shrink-0 rounded-full flex items-center justify-center", mood.ring)}>
                      <Icon className="w-3.5 h-3.5" />
                    </div>
                    <div className="min-w-0 flex-1 pt-0.5">
                      {m.content.trim().length > 0 && (
                        <p className={cn(
                          "font-serif text-foreground/90 whitespace-pre-wrap",
                          i === 0 ? "text-[19px] leading-[1.7]" : "text-[16.5px] leading-[1.65]"
                        )}>
                          {renderProse(m.content)}
                        </p>
                      )}
                      {m.suggestions && m.suggestions.length > 0 && !m.decided && (
                        <SuggestionCard
                          mood={mood}
                          suggestions={m.suggestions}
                          picked={picked[i] ?? m.suggestions.map(() => true)}
                          busy={busy}
                          onToggle={(j, v) =>
                            setPicked((p) => ({
                              ...p,
                              [i]: (p[i] ?? m.suggestions!.map(() => true)).map((x, k) => (k === j ? v : x)),
                            }))
                          }
                          onApprove={() => approve(i)}
                          onSkip={() => skip(i)}
                          t={t}
                        />
                      )}
                      <ResultChips results={m.results} decided={m.decided} t={t} />
                    </div>
                  </div>
                </div>
              );
            }

            // The user's reply — their own contained note, end-aligned.
            return (
              <div key={i} className="flex justify-end animate-companion-enter motion-reduce:animate-none">
                <div className="max-w-[80%] rounded-2xl rounded-ee-md bg-card/90 backdrop-blur-sm border border-border/70 shadow-sm px-4 py-2.5">
                  <p className="text-[14.5px] leading-relaxed text-foreground whitespace-pre-wrap">
                    {m.content}
                  </p>
                </div>
              </div>
            );
          })}

          {/* The companion is composing — a breathing presence, not typing dots */}
          {busy && (
            <div className="flex gap-3 items-center animate-companion-enter motion-reduce:animate-none">
              <div className={cn(
                "h-7 w-7 rounded-full flex items-center justify-center animate-pulse motion-reduce:animate-none",
                mood.ring, mood.glow
              )}>
                <Icon className="w-3.5 h-3.5 opacity-70" />
              </div>
            </div>
          )}

          {error && (
            <p className="text-xs text-destructive text-center" role="alert">
              {t("ritual_error")}{" "}
              <button
                className="underline underline-offset-2 cursor-pointer"
                onClick={() => turn(history(messages))}
              >
                {t("ritual_retry")}
              </button>
            </p>
          )}

          {/* Closing seal */}
          {closed && (
            <div className="text-center pt-8 pb-4 space-y-4 animate-companion-enter motion-reduce:animate-none">
              <div className={cn("inline-flex rounded-full p-4", mood.ring, mood.glow)}>
                <Check className="w-6 h-6" />
              </div>
              {intention && (
                <p className="font-serif italic text-[17px] leading-relaxed text-foreground/90 max-w-md mx-auto">
                  “{renderProse(intention)}”
                </p>
              )}
              <div>
                <Button size="sm" className="rounded-full px-6" onClick={onClose}>
                  {t("ritual_done")}
                </Button>
              </div>
            </div>
          )}
        </div>

        {/* Composer — a floating bar that stays out of the way of the page */}
        {!closed && (
          <form
            onSubmit={send}
            className="flex items-center gap-1.5 rounded-full border border-border/70 bg-card/80 backdrop-blur-md shadow-lg shadow-black/[0.04] ps-5 pe-1.5 py-1.5"
          >
            <input
              ref={inputRef}
              value={input}
              onChange={(e) => setInput(e.target.value)}
              placeholder={t("ritual_input_placeholder")}
              disabled={busy}
              autoFocus
              className="flex-1 bg-transparent text-[14.5px] text-foreground placeholder:text-muted-foreground/70 focus:outline-none disabled:opacity-60 min-w-0"
            />
            <Button
              type="submit"
              size="sm"
              className="rounded-full h-9 w-9 p-0 flex-shrink-0"
              disabled={busy || !input.trim()}
              aria-label={t("ritual_input_placeholder")}
            >
              <ArrowUp className="w-4 h-4" />
            </Button>
          </form>
        )}
      </div>
    </div>
  );

  return createPortal(overlay, document.body);
};

// ── Suggestion batch → approval card ─────────────────────────────────────────
// Proposed acts, each with its own face; nothing is real until the tap.

const SuggestionCard: React.FC<{
  mood: Mood;
  suggestions: RitualSuggestion[];
  picked: boolean[];
  busy: boolean;
  onToggle: (i: number, v: boolean) => void;
  onApprove: () => void;
  onSkip: () => void;
  t: (k: string, o?: any) => string;
}> = ({ mood, suggestions, picked, busy, onToggle, onApprove, onSkip, t }) => (
  <div className={cn(
    "mt-4 rounded-2xl border bg-card/80 backdrop-blur-sm shadow-sm overflow-hidden",
    mood.card
  )}>
    <p className="text-[10.5px] font-semibold uppercase tracking-[0.14em] text-muted-foreground/70 px-4 pt-3 pb-1">
      {t("ritual_suggested")}
    </p>
    <div className="px-2 pb-1">
      {suggestions.map((s, j) => {
        const KindIcon = suggestionIcon(s);
        const on = picked[j] ?? true;
        return (
          <label
            key={j}
            className={cn(
              "flex items-center gap-3 rounded-xl px-2 py-2 cursor-pointer transition-colors hover:bg-black/[0.03]",
              !on && "opacity-50"
            )}
          >
            <Checkbox checked={on} onCheckedChange={(v) => onToggle(j, Boolean(v))} />
            <span className={cn("h-6 w-6 flex-shrink-0 rounded-full flex items-center justify-center", mood.ring)}>
              <KindIcon className="w-3 h-3" />
            </span>
            <span className="text-sm text-foreground leading-snug">{s.label}</span>
          </label>
        );
      })}
    </div>
    <div className="flex items-center gap-2 px-4 py-2.5 border-t border-border/50 bg-black/[0.015]">
      <Button size="sm" className="rounded-full" disabled={busy} onClick={onApprove}>
        <Check className="w-3.5 h-3.5 me-1" />
        {t("ritual_approve")}
      </Button>
      <Button
        size="sm"
        variant="ghost"
        className="rounded-full text-muted-foreground"
        disabled={busy}
        onClick={onSkip}
      >
        {t("ritual_skip")}
      </Button>
    </div>
  </div>
);

const ResultChips: React.FC<{
  results?: RitualExecuteResult[];
  decided?: boolean;
  t: (k: string) => string;
}> = ({ results, decided, t }) => {
  if (results && results.length > 0) {
    return (
      <div className="flex flex-wrap gap-1.5 mt-3">
        {results.map((r, j) => {
          // Failed chips must show why — an unexplained red pill leaves the
          // user with nothing to act on.
          const showError = !r.ok && r.error;
          return (
            <span
              key={j}
              title={showError ? r.error : undefined}
              className={cn(
                "inline-flex items-center gap-1 rounded-full border px-2.5 py-1 text-[11px]",
                r.ok
                  ? "border-emerald-200 bg-emerald-50 text-emerald-800"
                  : "border-rose-200 bg-rose-50 text-rose-700"
              )}
            >
              {r.ok ? <Check className="w-3 h-3" /> : <X className="w-3 h-3" />}
              {r.label}
              {showError && <span className="opacity-75"> — {r.error}</span>}
            </span>
          );
        })}
      </div>
    );
  }
  if (decided) {
    return <p className="text-[11px] text-muted-foreground mt-2">{t("ritual_skipped")}</p>;
  }
  return null;
};

export default RitualCard;
